import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, ArrowRight, Home } from "lucide-react";
import { useI18n } from "@/i18n";

/**
 * Site-wide thin bar shown directly under the Navbar on every internal page.
 * Provides a one-tap path back to the previous page (preserving scroll position
 * via the browser history) and a fallback "Home" link. Hidden on pages where
 * it would be redundant (home, login, OAuth callback) — configured in App.js.
 */
export default function PageBackBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isRTL } = useI18n();
  // Hide on these routes — the navbar already gives a clear path home.
  const HIDDEN = new Set(["/", "/login", "/auth/callback"]);
  if (HIDDEN.has(location.pathname)) return null;

  const BackIcon = isRTL ? ArrowRight : ArrowLeft;
  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/");
  };

  return (
    <div className="border-b border-[#2A2421]/10 bg-[#F9F6F0]/90 backdrop-blur sticky top-14 z-30" data-testid="page-back-bar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-2 flex items-center gap-2">
        <button
          type="button"
          onClick={goBack}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs text-[#2A2421] hover:bg-[#C86240] hover:text-white transition-colors"
          data-testid="page-back-bar-back"
        >
          <BackIcon className="w-3.5 h-3.5" />
          <span>{isRTL ? "رجوع" : "Back"}</span>
        </button>
        <span className="text-[#2A2421]/20 text-xs">·</span>
        <button
          type="button"
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs text-[#5C544F] hover:bg-[#2A2421]/5 transition-colors"
          data-testid="page-back-bar-home"
        >
          <Home className="w-3.5 h-3.5" />
          <span>{isRTL ? "الرئيسية" : "Home"}</span>
        </button>
      </div>
    </div>
  );
}
